"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { CONSENT_OPEN_EVENT, getConsent, setConsent } from "@/lib/consent";

/**
 * First-party cookie-consent banner.
 *
 * On mount it reads the stored choice. If the visitor has already accepted
 * or rejected, nothing renders. Otherwise a fixed bottom banner offers
 * Accept / Reject. All trackers ship denied by default (Google Consent Mode
 * v2 defaults, Meta Pixel `revoke`, PostHog opt-out-by-default), so rejecting
 * simply records the choice; accepting flips the live grants without a reload.
 */
export default function ConsentBanner() {
  // Render nothing until we've read localStorage on the client — avoids an
  // SSR/CSR mismatch and a flash of the banner for visitors who already chose.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getConsent() === null) setVisible(true);

    // The footer "Cookie Preferences" link reopens the banner so visitors can
    // change their choice after the fact.
    const reopen = () => setVisible(true);
    window.addEventListener(CONSENT_OPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, reopen);
  }, []);

  function handleAccept() {
    setConsent("granted");

    // Fire the live grants without requiring a reload. Each tracker queues
    // its events until granted, so these flush anything captured cookielessly.
    window.gtag?.("consent", "update", {
      ad_storage: "granted",
      analytics_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });
    window.fbq?.("consent", "grant");

    // PostHog was initialized with opt_out_capturing_by_default — opt in now.
    void import("posthog-js").then(({ default: posthog }) => {
      posthog.opt_in_capturing?.();
    });

    setVisible(false);
  }

  function handleReject() {
    setConsent("denied");

    // The banner can be reopened from the footer after a prior Accept, so
    // rejecting must actively revoke — not just rely on the denied defaults.
    window.gtag?.("consent", "update", {
      ad_storage: "denied",
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.fbq?.("consent", "revoke");
    void import("posthog-js").then(({ default: posthog }) => {
      posthog.opt_out_capturing?.();
    });

    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="consent-banner-in fixed inset-x-0 bottom-0 z-[100] border-t border-white/10 bg-black/95 backdrop-blur-sm"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
        <p className="text-sm leading-relaxed text-gray-300">
          We use cookies for analytics and advertising to improve your
          experience.{" "}
          <Link
            href="/privacy"
            className="font-semibold text-brand-orange underline underline-offset-2 hover:text-brand-orange-light"
          >
            Privacy Policy
          </Link>
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={handleReject}
            className="px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-300 transition-colors hover:text-white"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="whitespace-nowrap bg-brand-orange-dark px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-orange"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
